import { useState, useEffect, useRef } from 'react'
import { searchActors, profileUrl } from '../api/tmdb'
import { ACTOR_POOL } from '../utils/dailyPuzzleGen'
import BrandLogo from './BrandLogo'
import {
  IconAny, IconActor, IconActress, IconSearch, IconDice, IconX, IconLock,
} from './icons'
import {
  subscribeToGame,
  lockInActor,
  resetActorLock,
  assignActorsAndStart,
} from '../utils/firebaseGame'

function shuffleArray(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const GENDER_FILTERS = [
  { value: 'any',    label: 'Any',     Icon: IconAny },
  { value: 'male',   label: 'Actor',   Icon: IconActor },
  { value: 'female', label: 'Actress', Icon: IconActress },
]

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

/**
 * Phase C – blind actor selection for H2H.
 * Props:
 *   gameId      – Firestore game document ID
 *   playerRole  – 'p1' | 'p2' (from the Firebase lobby)
 *   onGameReady – called with { startActor, goalActor } when status → 'playing'
 *   onAbandon   – called when the player abandons
 */
export default function H2HActorPicker({ gameId, playerRole, gameType = 'h2h', onGameReady, onAbandon }) {
  // ── Actor selection ──────────────────────────────────
  const [pickedActor, setPickedActor]   = useState(null)
  const [isLocked, setIsLocked]         = useState(false)
  const [locking, setLocking]           = useState(false)

  // 'snap'    – this player locked in the same actor as opponent, must repick
  // 'waiting' – opponent locked in the same actor and is repicking; hang tight
  // null      – normal state
  const [snapState, setSnapState] = useState(null)

  // ── Search ──────────────────────────────────────────
  const [query, setQuery]               = useState('')
  const [results, setResults]           = useState([])
  const [searching, setSearching]       = useState(false)
  const [loadingRandom, setLoadingRandom] = useState(false)
  const [genderFilter, setGenderFilter] = useState('any')

  // ── Game doc ─────────────────────────────────────────
  const [game, setGame] = useState(null)

  // ── Refs ─────────────────────────────────────────────
  const queueRef      = useRef({ filter: 'any', queue: [] })
  const inputRef      = useRef(null)
  const assigningRef  = useRef(false)   // prevent duplicate assignActorsAndStart calls (P1 only)
  const advancingRef  = useRef(false)   // prevent duplicate onGameReady calls
  const snapStateRef  = useRef(null)    // mirrors snapState without closure staleness
  const isLockedRef   = useRef(false)   // mirrors isLocked without closure staleness
  const lockedAtRef   = useRef(null)    // ms timestamp written to Firestore on lock-in

  // ── Search ──────────────────────────────────────────
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await searchActors(query.trim())
        setResults(data.filter(p => p.profile_path).slice(0, 12))
      } catch { setResults([]) }
      finally  { setSearching(false) }
    }, 350)
    return () => clearTimeout(t)
  }, [query])

  // ── Firestore subscription ───────────────────────────
  useEffect(() => {
    const unsub = subscribeToGame(gameId, setGame)
    return () => unsub()
  }, [gameId])

  // ── React to game updates ────────────────────────────
  useEffect(() => {
    if (!game) { console.log('[H2HActorPicker] no game yet, skipping'); return }

    const { p1, p2, status } = game
    const otherKey    = playerRole === 'p1' ? 'p2' : 'p1'
    const myPlayer    = game[playerRole]
    const otherPlayer = game[otherKey]

    console.log('[H2HActorPicker] game update — role:', playerRole,
      '| status:', status,
      '| p1.ready:', p1?.ready, '| p2.ready:', p2?.ready,
      '| p1.actor:', p1?.actor?.name, '| p2.actor:', p2?.actor?.name,
      '| advancingRef:', advancingRef.current, '| assigningRef:', assigningRef.current)

    // ① status → 'playing': always checked first, independently of assigningRef
    if (status === 'playing') {
      if (advancingRef.current) {
        console.log('[H2HActorPicker] status=playing but already called onGameReady — skipping duplicate')
        return
      }
      console.log('[H2HActorPicker] ✅ status=playing — calling onGameReady')
      advancingRef.current = true
      onGameReady({ startActor: game.startActor, goalActor: game.goalActor })
      return
    }

    // ② Clear 'waiting' snap message when opponent has unlocked (repicking)
    if (!otherPlayer?.ready && snapStateRef.current === 'waiting') {
      console.log('[H2HActorPicker] opponent unlocked — clearing waiting state')
      snapStateRef.current = null
      setSnapState(null)
    }

    // ③ Both locked in — check for duplicate or assign
    if (p1?.ready && p2?.ready) {
      console.log('[H2HActorPicker] both ready — p1:', p1.actor?.name, '| p2:', p2.actor?.name)

      if (p1.actor?.id === p2.actor?.id) {
        // ── DUPLICATE ────────────────────────────────
        const myLockedAt    = myPlayer?.lockedAt    ?? 0
        const otherLockedAt = otherPlayer?.lockedAt ?? 0
        const iAmLater =
          myLockedAt > otherLockedAt ||
          (myLockedAt === otherLockedAt && playerRole === 'p2')

        console.log('[H2HActorPicker] DUPLICATE actor — myLockedAt:', myLockedAt,
          '| otherLockedAt:', otherLockedAt, '| iAmLater:', iAmLater,
          '| isLockedRef:', isLockedRef.current, '| snapStateRef:', snapStateRef.current)

        if (iAmLater && isLockedRef.current && snapStateRef.current !== 'snap') {
          console.log('[H2HActorPicker] I am later locker — resetting my lock')
          resetActorLock(gameId, playerRole).catch((err) => {
            console.error('[H2HActorPicker] resetActorLock failed:', err)
          })
          setPickedActor(null)
          isLockedRef.current = false
          setIsLocked(false)
          lockedAtRef.current = null
          snapStateRef.current = 'snap'
          setSnapState('snap')
        } else if (!iAmLater && snapStateRef.current !== 'waiting') {
          console.log('[H2HActorPicker] I am earlier locker — waiting for opponent to repick')
          snapStateRef.current = 'waiting'
          setSnapState('waiting')
        } else {
          console.log('[H2HActorPicker] DUPLICATE — no action taken (already handled)')
        }
      } else {
        // ── DIFFERENT ACTORS: P1 assigns start / goal ──
        console.log('[H2HActorPicker] different actors — playerRole:', playerRole)
        if (playerRole === 'p1') {
          if (assigningRef.current) {
            console.log('[H2HActorPicker] P1 already called assignActorsAndStart — skipping duplicate')
            return
          }
          console.log('[H2HActorPicker] I am P1 — calling assignActorsAndStart')
          assigningRef.current = true
          const actors = [p1.actor, p2.actor]
          const [start, goal] =
            Math.random() < 0.5 ? actors : [actors[1], actors[0]]
          console.log('[H2HActorPicker] assigning start:', start?.name, '| goal:', goal?.name)
          assignActorsAndStart(gameId, start, goal, gameType)
            .then(() => console.log('[H2HActorPicker] assignActorsAndStart succeeded'))
            .catch((err) => {
              console.error('[H2HActorPicker] assignActorsAndStart FAILED:', err)
              assigningRef.current = false
            })
        } else {
          console.log('[H2HActorPicker] I am P2 — waiting for P1 to assign actors')
        }
      }
    } else {
      console.log('[H2HActorPicker] not both ready yet — waiting')
    }
  }, [game]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────
  const handlePickActor = (actor) => {
    setPickedActor(actor)
    setQuery('')
    setResults([])
    // Clear snap message so the player can proceed after repicking
    snapStateRef.current = null
    setSnapState(null)
  }

  const handleClearPick = () => setPickedActor(null)

  const handleLockIn = async () => {
    if (!pickedActor || isLockedRef.current || locking) return
    setLocking(true)
    const lockedAt = Date.now()
    lockedAtRef.current = lockedAt
    const actorSnap = {
      id:           pickedActor.id,
      name:         pickedActor.name,
      profile_path: pickedActor.profile_path,
    }
    try {
      await lockInActor(gameId, playerRole, actorSnap, lockedAt)
      isLockedRef.current = true
      setIsLocked(true)
    } catch {
      // Firestore write failed — silently revert so the player can try again
      lockedAtRef.current = null
    } finally {
      setLocking(false)
    }
  }

  const handleRandom = async () => {
    setLoadingRandom(true)
    try {
      const current = queueRef.current
      let queue = current.queue
      if (current.filter !== genderFilter || queue.length === 0) {
        const pool =
          genderFilter === 'any'
            ? ACTOR_POOL
            : ACTOR_POOL.filter(a => a.gender === genderFilter)
        queue = shuffleArray(pool)
      }
      const entry = queue[queue.length - 1]
      queueRef.current = { filter: genderFilter, queue: queue.slice(0, -1) }
      if (!entry) return
      const data = await searchActors(entry.name)
      const actor =
        data.find(r => r.name.toLowerCase() === entry.name.toLowerCase()) || data[0]
      if (actor) handlePickActor(actor)
    } catch {} // eslint-disable-line no-empty
    finally { setLoadingRandom(false) }
  }

  // ── Derived UI state ─────────────────────────────────
  const isCoop = gameType === 'coop'
  let subtitle = isCoop ? 'Your co-star is choosing right now…' : 'Your opponent is choosing right now…'
  if (isLocked)                subtitle = isCoop ? 'Locked in! Waiting for co-star…' : 'Locked in! Waiting for opponent…'
  if (snapState === 'snap')    subtitle = "Snap! You both picked the same actor. Pick again."
  if (snapState === 'waiting') subtitle = isCoop ? 'Your co-star needs to repick. Hang tight…' : 'Your opponent needs to repick. Hang tight…'

  const showSearch = !isLocked && snapState !== 'waiting'

  return (
    <div className="setup">
      <header className="setup__header">
        <h1 className="app-title"><BrandLogo /></h1>
        <p className="app-subtitle">{isCoop ? 'Co-Op' : 'Head to Head'}</p>
      </header>

      {/* Status heading */}
      <div className={`h2h-pick-heading${snapState ? ' h2h-pick-heading--alert' : ''}`}>
        <p className="h2h-pick-heading__title">Pick Your Actor</p>
        <p className="h2h-pick-heading__sub">{subtitle}</p>
      </div>

      {/* Selected actor card */}
      {pickedActor && (
        <div className={`h2h-pick-card${isLocked ? ' h2h-pick-card--locked' : ''}`}>
          <img
            className="h2h-pick-card__img"
            src={profileUrl(pickedActor.profile_path) || PLACEHOLDER}
            alt={pickedActor.name}
            onError={(e) => { e.target.src = PLACEHOLDER }}
          />
          <div className="h2h-pick-card__info">
            <span className="h2h-pick-card__name">{pickedActor.name}</span>
            {isLocked && (
              <span className="h2h-pick-card__lock" aria-label="locked">
                <IconLock size={14} aria-hidden="true" />
              </span>
            )}
          </div>
          {!isLocked && (
            <button
              type="button"
              className="setup__slot-clear"
              onClick={handleClearPick}
              aria-label="Clear selection"
            >
              <IconX size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {/* Search area — hidden while locked or while waiting for opponent to repick */}
      {showSearch && (
        <div className="setup__search-area">
          <div className="gender-filter">
            {GENDER_FILTERS.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                className={`gender-filter__btn${genderFilter === value ? ' gender-filter__btn--active' : ''}`}
                onClick={() => setGenderFilter(value)}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="setup__search-box">
            <span className="setup__search-icon" aria-hidden="true">
              <IconSearch size={18} />
            </span>
            <input
              ref={inputRef}
              className="setup__input"
              type="text"
              placeholder="Search for an actor…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className="setup__random-btn"
              onClick={handleRandom}
              disabled={loadingRandom}
              title="Pick a random actor"
            >
              {loadingRandom
                ? <div className="spinner spinner--sm" />
                : <IconDice size={20} aria-hidden="true" />}
            </button>
            {query && (
              <button
                type="button"
                className="setup__clear-input"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <IconX size={14} aria-hidden="true" />
              </button>
            )}
          </div>

          {searching && <div className="setup__searching"><div className="spinner" /></div>}

          {results.length > 0 && (
            <div className="setup__results">
              {results.map(actor => (
                <button
                  key={actor.id}
                  className="setup__result-card"
                  onClick={() => handlePickActor(actor)}
                >
                  <img
                    src={profileUrl(actor.profile_path, 'w185') || PLACEHOLDER}
                    alt={actor.name}
                    onError={(e) => { e.target.src = PLACEHOLDER }}
                  />
                  <span>{actor.name}</span>
                </button>
              ))}
            </div>
          )}

          {!searching && query.length >= 2 && results.length === 0 && (
            <p className="setup__no-results">No actors found for "{query}"</p>
          )}
        </div>
      )}

      {/* Lock In */}
      {pickedActor && !isLocked && (
        <button
          className="btn-primary setup__start-btn"
          onClick={handleLockIn}
          disabled={locking}
        >
          {locking ? 'Locking…' : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconLock size={16} aria-hidden="true" />
              <span>Lock In</span>
            </span>
          )}
        </button>
      )}

      <button
        type="button"
        className="btn-ghost actor-picker__cancel"
        onClick={onAbandon}
      >
        Abandon
      </button>
    </div>
  )
}
